import { useEffect, useState } from 'react';
// import PropTypes from 'prop-types';

/**
 * Object containing the logged in user, loading state, and error state.
 * @typedef {Object} IUseAuthenticate
 */
export type IUseAuthenticate = {
    user: User | null;
    loading: boolean;
    error: string | null;
};

/**
 * Configuration object for the useAuthenticate hook.
 * apiBaseUrl: The base URL for the API.
 * tokenParam: The query parameter used to pass the JWT token. Defaults to `token`
 * @typedef {Object} AuthConfig
 */
type AuthConfig = {
    apiBaseUrl: string;
    tokenParam: string;
};

export type User = {
    id: string;
    name: string;
    email: string;
};

const defaultConfig: AuthConfig = {
    apiBaseUrl: 'https://id.hey.gg',
    tokenParam: 'token'
};

/**
 * Custom hook for authenticating a user via a jwt token in the URL (the `token` query param) or local storage.
 * @param {AuthConfig} config The configuration object.
 * @returns {IUseAuthenticate} An object containing the user, loading state, and error state.
 * @example
 *   const ExampleComponent = () => {
 *     const { user, loading, error } = useAuthenticate({ apiBaseUrl: 'https://api.example.com' });
 *
 *     if (user) {
 *       return <div>Welcome, {user.name}!</div>
 *    }
 *   }
 */
export const useAuthenticate = (config: AuthConfig ): IUseAuthenticate => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    config = { ...defaultConfig, ...config };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = searchParams.get(config.tokenParam);

        if (tokenFromUrl) {
            localStorage.setItem('jwtToken', tokenFromUrl);
            // Update the URL to remove the token without reloading the page
            searchParams.delete(config.tokenParam);
            const newUrl = window.location.pathname + '?' + searchParams.toString();
            window.history.replaceState(null, '', newUrl);
            authenticateUser(tokenFromUrl);
        } else {
            const tokenFromStorage = localStorage.getItem('jwtToken');
            if (tokenFromStorage) {
                authenticateUser(tokenFromStorage);
            } else {
                setLoading(false);
            }
        }
    }, []);

    const authenticateUser = (token: string) => {
        fetch(`${config.apiBaseUrl}/api/user`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            if (!response.ok) {
                throw new Error('Failed to fetch user details');
            }
            return response.json();
        })
        .then(data => {
            setUser(data);
            setLoading(false);
        })
        .catch(err => {
            setError(err.message);
            setLoading(false);
        });
    };

    return { user, loading, error };
};

